/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CardMetadata, CardSkills, ProductCard, breakpoints } from '@coursera/cds-core';
import type { ProductCard as ProductCardTrackingData, ProductType, Recommender } from '@coursera/event-pulse-types';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import SiteMiscExperiments from 'bundles/epic/clients/siteMisc';
import ProductCardRatings from 'bundles/product-card/components/ProductCardRatings';
import ProductCardReviews from 'bundles/product-card/components/ProductCardReviews';
import StopPropagationWrapper from 'bundles/product-card/components/StopPropagationWrapper';
import {
  capitalizeAndTranslateEachWordInEntityType,
  eventingV3ProductType,
  extractEventId,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import useOnVisible from 'bundles/product-card/hooks/useOnVisible';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';

const styles = {
  leftAlignProductCardTags: css`
    ${breakpoints.up('md')} {
      .cds-ProductCard-listCard {
        .cds-ProductCard-statusTags {
          position: absolute;
          left: 0;
          right: unset;
          top: var(--cds-spacing-100);
          padding-left: var(--cds-spacing-100);
          max-width: 298px;
          z-index: 1;
        }
      }
    }
  `,

  rightAlignProductCardTags: css`
    ${breakpoints.up('sm')} {
      .cds-ProductCard-statusTagsOverlay {
        left: unset !important;
        right: 0;
      }
    }
  `,

  cardContainer: css`
    min-height: 200px;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: pointer;

    .cds-ProductCard-partnerLogos img {
      vertical-align: unset;
    }

    .cds-CommonCard-previewImage {
      position: relative;
    }
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,

  // Temporary: Lawrie mentioned .cds-CommonCard-creditType will be removed in the future
  creditTypeGapTest1: css`
    .cds-CommonCard-creditType {
      gap: 4px !important;
    }
  `,

  buttons: css`
    margin-top: var(--cds-spacing-200);

    button.pathways-recommendation-coach {
      box-shadow: none;
    }

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-150);

      > span {
        width: 100%;
        flex-direction: column-reverse;

        div.with-enroll-modal > button {
          width: 100%;
        }
      }
    }
  `,
};

type Tag = string | React.ReactElement;

type Props = {
  adminContent?: React.ReactNode;
  customMetadata?: string[];
  customOnClick?: () => void;
  ImageComponent?: React.ReactNode;
  name: string;
  linkProps?: { href?: string; target?: string; rel?: string; component?: React.ElementType };
  titleComponent?: 'h3' | 'h4';
  partners?: { id: string; logo?: string; name: string }[];
  skills?: string[];
  bodyContent?: React.ReactNode;
  numProductRatings?: number;
  avgProductRating?: number;
  productType?: (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];
  productDifficultyLevel?: string;
  productDuration?: string;
  statusTags?: { tags: Tag[]; tagNames?: string[] };
  badge?: React.ReactNode;
  variant?: 'list' | 'grid';
  size?: 'compact' | 'standard';
  id?: string;
  slug?: string;
  cardIndex?: number;
  indexName?: string;
  recommender?: Recommender;
  trackingProps?: {
    trackedLinkProps?: {
      trackingName: string;
      withVisibilityTracking: boolean;
      requireFullyVisible: boolean;
      data: Record<string, unknown>;
      refAlt?: React.RefObject<HTMLAnchorElement>;
    };
    additionalProductCardTrackingData?: Partial<ProductCardTrackingData>;
    additionalV3TrackingData?: Record<string, unknown>;
  };
  isMetadataAboveProductRating?: boolean;
  buttons?: Array<React.ReactElement>;
};

const LearningProductCard = ({
  adminContent,
  customMetadata,
  customOnClick,
  ImageComponent,
  name,
  titleComponent,
  linkProps,
  partners,
  skills,
  bodyContent,
  numProductRatings,
  avgProductRating,
  productType,
  productDifficultyLevel,
  productDuration,
  statusTags,
  badge,
  variant = 'grid',
  size = 'standard',
  id,
  slug,
  cardIndex,
  indexName,
  recommender,
  trackingProps,
  isMetadataAboveProductRating = false,
  buttons,
}: Props) => {
  const track = useTracker();

  const getTrackingProductId = (productId: string | undefined): string => {
    if (typeof productId !== 'string') {
      return '';
    }
    if (productId.startsWith('child~')) {
      return productId; // Preserve "child~" prefix for product IDs in tracking events
    }
    return extractEventId(productId) || '';
  };

  const trackingData: EventData<'click_product_card'> = {
    product: {
      id: getTrackingProductId(id),
      name,
      slug: slug ?? linkProps?.href ?? '',
      type: eventingV3ProductType(productType) as ProductType,
    },
    productCard: {
      ...(typeof trackingProps?.trackedLinkProps?.data.productCard === 'object'
        ? trackingProps?.trackedLinkProps?.data.productCard
        : {}),
      index: cardIndex ?? 0,
      ...(indexName && { algoliaIndexName: indexName }),
      ...(recommender && { recommender }),
      tags: statusTags?.tagNames,
      ...(trackingProps?.additionalProductCardTrackingData?.pageConfigComponent && {
        pageConfigComponent: trackingProps?.additionalProductCardTrackingData?.pageConfigComponent,
      }),
    },
    ...trackingProps?.additionalV3TrackingData,
  };

  const trackedCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    trackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  const experimentVariant = SiteMiscExperiments.preview('productCardTest1');
  useOnVisible(
    trackedCardRef,
    () => {
      // Impression: get() returns same variant as preview() but records the experiment show event
      SiteMiscExperiments.get('productCardTest1');
    },
    { fullyVisible: true, initialDelay: 1000, once: true }
  );

  const cardMetadata =
    customMetadata ||
    [productDifficultyLevel, capitalizeAndTranslateEachWordInEntityType(productType), productDuration].filter(Boolean);

  // Rendering list cards with even a single status tag can cause styling issues with longer-typed
  // languages for desktop widths(see https://coursera.atlassian.net/browse/MERCH-1660). As a
  // workaround, we left-align tags and assign a max-width slightly less than the preview image.
  const listCardWithStatusTags = variant === 'list' && statusTags?.tags?.length && statusTags?.tags?.length >= 1;

  // Wrap buttons to add stopPropagation to their click events
  const buttonsWithStopPropagation = buttons?.map((button) => {
    return React.isValidElement(button) ? <StopPropagationWrapper>{button}</StopPropagationWrapper> : button;
  });

  return (
    <div
      css={[
        styles.cardContainer,
        listCardWithStatusTags ? styles.leftAlignProductCardTags : styles.rightAlignProductCardTags,
        variant === 'list' ? styles.listCardContainer : null,
        experimentVariant === 'test1' ? styles.creditTypeGapTest1 : null,
      ]}
      data-testid="product-card-cds"
    >
      <ProductCard
        experimentVariant={experimentVariant}
        onClick={(event) => {
          track('click_product_card', trackingData);
          if (linkProps?.href) {
            return;
          }
          if (customOnClick) {
            event.preventDefault();
            event.stopPropagation();
            customOnClick();
          }
        }}
        ref={trackedCardRef}
        renderPreviewImage={() => ImageComponent}
        title={{
          name,
          linkProps,
          component: titleComponent,
          customLinkProps: trackingProps?.trackedLinkProps,
        }}
        partners={partners}
        body={
          (skills && skills.length > 0) || bodyContent ? (
            <>
              {skills && skills.length > 0 && <CardSkills skills={skills} />}
              {bodyContent}
            </>
          ) : undefined
        }
        footer={
          isMetadataAboveProductRating ? (
            <>
              {cardMetadata.length > 0 && <CardMetadata metadata={cardMetadata} />}
              {Boolean(avgProductRating) && Boolean(numProductRatings) && (
                <ProductCardRatings avgProductRating={avgProductRating} numProductRatings={numProductRatings} />
              )}
            </>
          ) : (
            <>
              {badge}
              {Boolean(avgProductRating) && Boolean(numProductRatings) && (
                <ProductCardReviews avgProductRating={avgProductRating} numProductRatings={numProductRatings} />
              )}
              {cardMetadata.length > 0 && <CardMetadata metadata={cardMetadata} />}
              {buttonsWithStopPropagation && buttonsWithStopPropagation.length > 0 && (
                <div css={styles.buttons}>{buttonsWithStopPropagation}</div>
              )}
            </>
          )
        }
        variant={variant}
        size={size}
        productType={productType || ''}
        statusTags={statusTags?.tags}
        adminContent={adminContent}
      />
    </div>
  );
};

export default LearningProductCard;
